import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

/**
 * Search-to-add picker for crew and backstage delivery destinations.
 *
 * Type a (partial) name; matching stops from both crew and backstage appear
 * in a dropdown and a click adds them. With an empty query the box lists all
 * available stops so it doubles as a browsable list. Already-added stops are
 * excluded. `onAdd` receives `{ id, name, type: 'crew' | 'backstage' }`.
 */
const DestinationPicker = ({
  crewLocations = [],
  backstageLocations = [],
  addedIds = [],
  onAdd,
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);

  const addedSet = useMemo(() => new Set(addedIds), [addedIds]);

  const options = useMemo(() => {
    const all = [
      ...crewLocations.map((l) => ({ id: l.id, name: l.name, type: 'crew' })),
      ...backstageLocations.map((l) => ({
        id: l.id,
        name: l.name,
        type: 'backstage',
      })),
    ];
    return all.sort((a, b) => a.name.localeCompare(b.name));
  }, [crewLocations, backstageLocations]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    // No cap: the dropdown scrolls (maxHeight below), so the full list is
    // reachable whether the search is empty or filtered. Already-added stops
    // stay in the list (rendered disabled with a hint) so a search for one
    // shows the match instead of "Keine Treffer".
    return options.filter((o) => (q ? o.name.toLowerCase().includes(q) : true));
  }, [options, query]);

  // Keep the highlighted row valid as results change.
  useEffect(() => {
    setHighlight((h) => Math.min(h, Math.max(results.length - 1, 0)));
  }, [results.length]);

  // Close the dropdown when clicking outside the component.
  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const addOption = (option) => {
    if (!option || addedSet.has(option.id)) return;
    onAdd(option);
    setQuery('');
    setHighlight(0);
    setOpen(true); // stay open so several stops can be added in a row
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      addOption(results[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className='position-relative' ref={wrapperRef}>
      <div className='input-group'>
        <span className='input-group-text'>
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </span>
        <input
          type='text'
          className='form-control'
          placeholder='Lieferstelle suchen…'
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </div>

      {open && (
        <ul
          className='list-group position-absolute w-100 shadow-sm'
          style={{ zIndex: 1000, maxHeight: '16rem', overflowY: 'auto' }}
        >
          {results.length === 0 ? (
            <li className='list-group-item text-muted'>Keine Treffer</li>
          ) : (
            results.map((option, index) => {
              const added = addedSet.has(option.id);
              return (
                <li
                  key={option.id}
                  role={added ? undefined : 'button'}
                  className={`list-group-item d-flex justify-content-between align-items-center ${
                    added
                      ? 'text-muted'
                      : `list-group-item-action ${
                          index === highlight ? 'active' : ''
                        }`
                  }`}
                  onMouseEnter={added ? undefined : () => setHighlight(index)}
                  // onMouseDown (not onClick) so the add fires before the input
                  // blur can close the dropdown.
                  onMouseDown={
                    added
                      ? undefined
                      : (e) => {
                          e.preventDefault();
                          addOption(option);
                        }
                  }
                >
                  <span>{option.name}</span>
                  {added ? (
                    <span className='badge bg-light text-muted border'>
                      bereits hinzugefügt
                    </span>
                  ) : (
                    <span
                      className={`badge ${
                        option.type === 'backstage' ? 'bg-info' : 'bg-secondary'
                      }`}
                    >
                      {option.type === 'backstage' ? 'Backstage' : 'Crew'}
                    </span>
                  )}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default DestinationPicker;
